import React, { useCallback, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Slider.css";

import p1 from "../Assets/poster.jpeg";
import p2 from "../Assets/poster2.jpeg";
import p3 from "../Assets/poster3.jpeg";
import p4 from "../Assets/poster4.webp";
import p5 from "../Assets/poster5.jpeg";
import p6 from "../Assets/demo_part.jpg";

import { API_BASE } from "../../config/api";
import { bindStorefrontRefresh } from "../../utils/storefrontRefresh";

const FALLBACK_CATEGORIES = [
  { name: "BRASS-CZ-COLOUR STONE (IMITATION)", imageUrl: "" },
  { name: "SILVER-PLAIN", imageUrl: "" },
  { name: "SILVER-CZ-COLOR STONE", imageUrl: "" },
  { name: "SILVER-CVD (LGD)", imageUrl: "" },
  { name: "SILVER-NATURAL DIAMOND", imageUrl: "" },
  { name: "GOLD-PLAIN", imageUrl: "" },
  { name: "GOLD-CZ-COLOR STONE", imageUrl: "" },
  { name: "GOLD-CVD (LGD)", imageUrl: "" },
  { name: "GOLD-NATURAL DIAMOND", imageUrl: "" },
  { name: "PLATINUM-PLAIN", imageUrl: "" },
  { name: "PLATINUM-CVD (LGD)", imageUrl: "" },
  { name: "PLATINUM-NATURAL DIAMOND", imageUrl: "" },
  { name: "PLATINUM-GOLD-PLAIN", imageUrl: "" },
  { name: "PLATINUM-GOLD-STUDDED (LGD/NATURAL)", imageUrl: "" },
];

const DEFAULT_IMAGES = [p1, p2, p3, p4, p5, p6];

const MATERIAL_PATTERNS = [
  { match: /^PLATINUM-GOLD/i, label: "Platinum Gold" },
  { match: /^PLATINUM/i, label: "Platinum" },
  { match: /^SILVER/i, label: "Silver" },
  { match: /^GOLD/i, label: "Gold" },
  { match: /^BRASS/i, label: "Brass" },
];

const CATEGORY_STORIES = [
  {
    match: /STUDDED/i,
    suffix: "Studded",
    eyebrow: "Bridal Jewellery",
    subtitle: "Layered brilliance for grand celebrations and heirloom moments.",
  },
  {
    match: /NATURAL DIAMOND/i,
    suffix: "Diamond",
    eyebrow: "Fine Jewellery",
    subtitle: "Polished sparkle designed for elegant everyday radiance.",
  },
  {
    match: /CVD|LGD/i,
    suffix: "Lab Grown",
    eyebrow: "Modern Heirloom",
    subtitle: "Contemporary luminosity with a clean, refined finish.",
  },
  {
    match: /CZ|COLOU?R STONE/i,
    suffix: "Colour Stone",
    eyebrow: "Statement Jewellery",
    subtitle: "Softly vibrant pieces curated for elevated occasions.",
  },
  {
    match: /PLAIN/i,
    suffix: "Plain",
    eyebrow: "Everyday Luxury",
    subtitle: "Minimal silhouettes with a graceful premium glow.",
  },
];

const toTitleCase = (value = "") =>
  String(value || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const humanizeCategoryName = (value = "") =>
  toTitleCase(
    String(value || "")
      .replace(/\([^)]*\)/g, "")
      .replace(/CVD/gi, "Lab Grown")
      .replace(/LGD/gi, "Lab Grown")
      .replace(/CZ/gi, "CZ")
      .replace(/COLOUR/gi, "Colour")
      .replace(/-/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );

const getMaterialLabel = (value = "") => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  const materialMatch = MATERIAL_PATTERNS.find(({ match }) => match.test(normalizedValue));

  if (materialMatch) {
    return materialMatch.label;
  }

  return humanizeCategoryName(normalizedValue.split("-")[0] || normalizedValue);
};

const getCategoryPresentation = (value = "") => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  const materialLabel = getMaterialLabel(normalizedValue);
  const matchedStory = CATEGORY_STORIES.find(({ match }) => match.test(normalizedValue));

  if (matchedStory) {
    return {
      eyebrow: matchedStory.eyebrow,
      title: `${materialLabel} ${matchedStory.suffix}`.trim(),
      subtitle: matchedStory.subtitle,
    };
  }

  return {
    eyebrow: "Signature Edit",
    title: humanizeCategoryName(normalizedValue),
    subtitle: "Curated jewellery for a soft, luminous statement.",
  };
};

const getImage = (item, index) => {
  if (item?.imageUrl?.trim()) {
    return item.imageUrl;
  }

  return DEFAULT_IMAGES[index % DEFAULT_IMAGES.length];
};

const CategorySlider = () => {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const navigate = useNavigate();

  const fetchCategories = useCallback(() => {
    fetch(`${API_BASE}/category-slider`)
      .then((response) => response.json())
      .then((data) => {
        const items = data?.items;

        if (Array.isArray(items) && items.length > 0) {
          setCategories(items);
          return;
        }

        setCategories(FALLBACK_CATEGORIES);
      })
      .catch(() => setCategories(FALLBACK_CATEGORIES));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => bindStorefrontRefresh(fetchCategories, { scopes: ["homepage"] }), [fetchCategories]);

  const handleCategoryClick = (categoryName) => {
    navigate(`/store?material=${encodeURIComponent(categoryName)}`);
  };

  const showSliderControls = categories.length > 1;

  return (
    <section className="lux-category-section" aria-labelledby="shop-by-category-heading">
      <div className="lux-category-shell">
        <header className="lux-category-header">
          <h2 id="shop-by-category-heading" className="lux-category-header__title">
            SHOP BY CATEGORY
          </h2>
          <div className="lux-category-header__divider" aria-hidden="true">
            <span />
            <i />
            <span />
          </div>
        </header>

        <div className="lux-category-slider-wrap">
          {showSliderControls ? (
            <>
              <button
                type="button"
                className="lux-category-nav lux-category-nav--prev"
                aria-label="View previous categories"
              >
                <FiChevronLeft />
              </button>
              <button
                type="button"
                className="lux-category-nav lux-category-nav--next"
                aria-label="View next categories"
              >
                <FiChevronRight />
              </button>
            </>
          ) : null}

          <Swiper
            className="lux-category-slider"
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={14}
            loop={categories.length > 4}
            navigation={
              showSliderControls
                ? {
                    prevEl: ".lux-category-nav--prev",
                    nextEl: ".lux-category-nav--next",
                  }
                : false
            }
            pagination={
              showSliderControls
                ? {
                    el: ".lux-category-pagination",
                    clickable: true,
                  }
                : false
            }
            autoplay={
              showSliderControls
                ? {
                    delay: 4200,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            breakpoints={{
              0: { slidesPerView: 1, spaceBetween: 14 },
              768: { slidesPerView: 2, spaceBetween: 16 },
              1024: { slidesPerView: 4, spaceBetween: 18 },
            }}
          >
            {categories.map((category, index) => {
              const presentation = getCategoryPresentation(category.name);

              return (
                <SwiperSlide key={`${category.name}-${index}`}>
                  <button
                    type="button"
                    className="lux-category-card"
                    onClick={() => handleCategoryClick(category.name)}
                    aria-label={`Explore ${presentation.title}`}
                  >
                    <div className="lux-category-card__media">
                      <img
                        src={getImage(category, index)}
                        alt={`${presentation.title} jewellery`}
                        className="lux-category-card__image"
                        loading="lazy"
                      />
                    </div>

                    <div className="lux-category-card__body">
                      <span className="lux-category-card__eyebrow">{presentation.eyebrow}</span>
                      <h3>{presentation.title}</h3>
                      <p>{presentation.subtitle}</p>
                      <span className="lux-category-card__cta">
                        Explore <FiArrowRight aria-hidden="true" />
                      </span>
                    </div>
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {showSliderControls ? <div className="lux-category-pagination" aria-hidden="true" /> : null}
        </div>
      </div>
    </section>
  );
};

export default CategorySlider;
